import {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  FilterSpecification,
  LayerProps,
} from 'shared/map-exports';
import {
  DEFAULT_VIEW_STATE,
  SELECTED_FEATURE_HIGHLIGHT_COLOR,
} from 'features/mapping/mapping.constants';
import { FONT_NAMES } from 'features/mapping/styles/constants';
import { AQI_LEVELS_LOWER_BOUNDS } from 'features/aqiSensors/aqiSensors.constants';
import { TFunction } from 'i18next';
import { TOOLTIP_LABEL_LAYOUT_STYLES } from 'features/mapping/mapping.styles';
import {
  AQI_COLOR_STOPS,
  AQI_OFFLINE_COLOR,
  AQI_SOURCE_LAYER,
  MIN_ICON_ZOOM_LEVEL,
  MIN_ICON_ZOOM_LEVEL_MODERATE_OR_ABOVE,
  MIN_LABEL_ZOOM_LEVEL,
} from './AQILayer.constants';

const isOfflineExpression = ['to-boolean', ['get', 'is_offline']];

const circleColorSpec: DataDrivenPropertyValueSpecification<string> = [
  'case',
  ['any', ['!=', ['typeof', ['get', 'aqi']], 'number'], isOfflineExpression],
  AQI_OFFLINE_COLOR,
  [
    'interpolate-hcl',
    ['linear'],
    ['get', 'aqi'],
    ...AQI_COLOR_STOPS.flatMap((stop) => [stop.value, stop.color]),
  ],
];

const isAqiModerateOrAbove: ExpressionSpecification = [
  '>=',
  ['get', 'aqi'],
  AQI_LEVELS_LOWER_BOUNDS.moderate,
];

const commonPointIconStyle: LayerProps = {
  type: 'circle',
  'source-layer': AQI_SOURCE_LAYER,
  paint: {
    'circle-color': circleColorSpec,
    'circle-stroke-color': '#FFFFFF',
  },
};

export const offlineOrGoodPointIconStyle: LayerProps = {
  ...commonPointIconStyle,
  minzoom: MIN_ICON_ZOOM_LEVEL,
  paint: {
    ...commonPointIconStyle.paint,
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_ICON_ZOOM_LEVEL,
      5,
      13,
      6,
    ],
    'circle-stroke-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_ICON_ZOOM_LEVEL,
      1.5,
      13,
      1.8,
    ],
  },
  filter: [
    'any',
    isOfflineExpression,
    ['<', ['get', 'aqi'], AQI_LEVELS_LOWER_BOUNDS.moderate],
  ],
};

export const moderateOrAbovePointIconStyle: LayerProps = {
  ...commonPointIconStyle,
  minzoom: MIN_ICON_ZOOM_LEVEL_MODERATE_OR_ABOVE,
  paint: {
    ...commonPointIconStyle.paint,
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_ICON_ZOOM_LEVEL_MODERATE_OR_ABOVE,
      4,
      MIN_ICON_ZOOM_LEVEL,
      5,
      13,
      6,
    ],
    'circle-stroke-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_ICON_ZOOM_LEVEL_MODERATE_OR_ABOVE,
      1.3,
      MIN_ICON_ZOOM_LEVEL,
      1.5,
      13,
      1.8,
    ],
  },
  filter: isAqiModerateOrAbove,
};

const commonPointBlurStyle: LayerProps = {
  type: 'circle',
  'source-layer': AQI_SOURCE_LAYER,
  paint: {
    'circle-color': circleColorSpec,
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      DEFAULT_VIEW_STATE.minZoom,
      8,
      MIN_ICON_ZOOM_LEVEL,
      18,
      13,
      26,
    ],
    'circle-blur': 1,
  },
};

// Style for blurs excluding the active sensor with min zoom level
export const getBasePointBlurStyle = (activeSensorId?: number): LayerProps => {
  const filter: FilterSpecification = ['!=', ['id'], activeSensorId ?? null];

  return {
    ...commonPointBlurStyle,
    minzoom: MIN_ICON_ZOOM_LEVEL,
    filter,
  };
};

// Style for blurs excluding the active sensor, offline sensors, and good AQI levels
export const getPointBlurStyle = (activeSensorId?: number): LayerProps => {
  const filter: FilterSpecification = [
    'all',
    ['!=', ['id'], activeSensorId ?? null],
    ['!', isOfflineExpression],
    isAqiModerateOrAbove,
  ];

  return {
    ...commonPointBlurStyle,
    filter,
  };
};

export const getLabelStyle = (t: TFunction): LayerProps => ({
  type: 'symbol',
  minzoom: MIN_LABEL_ZOOM_LEVEL,
  'source-layer': AQI_SOURCE_LAYER,
  layout: {
    'text-offset': [0, 2.25],
    ...TOOLTIP_LABEL_LAYOUT_STYLES,
    'text-field': [
      'case',
      isOfflineExpression,
      ['format', ['literal', t('common.offline')]],
      [
        'format',
        ['get', 'aqi'],
        { 'text-font': FONT_NAMES.bold },
        ['literal', ' AQI'],
        { 'font-scale': 0.8 },
      ],
    ],
    'icon-allow-overlap': true,
    'symbol-sort-key': [
      'case',
      isOfflineExpression,
      2, // lowest priority
      isAqiModerateOrAbove,
      0, // highest priority - renders above others
      1, // mid priority (0–50 AQI)
    ],
  },
  paint: {
    'icon-color': '#FFFFFF',
  },
  filter: [
    'any',
    ['==', ['typeof', ['get', 'aqi']], 'number'],
    isOfflineExpression,
  ],
});

export const getSelectedPointIconStyle = (
  activeSensorId?: number,
): LayerProps => {
  const filter: FilterSpecification = ['==', ['id'], activeSensorId ?? null];

  return {
    type: 'circle',
    'source-layer': AQI_SOURCE_LAYER,
    minzoom: MIN_ICON_ZOOM_LEVEL,
    paint: {
      'circle-radius': 0,
      'circle-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
      'circle-blur': 1,
    },
    filter,
  };
};

export const getSelectedPointIconModerateOrAboveStyle = (
  activeSensorId?: number,
): LayerProps => {
  const filter: FilterSpecification = [
    'all',
    ['==', ['id'], activeSensorId ?? null],
    isAqiModerateOrAbove,
  ];

  return {
    type: 'circle',
    'source-layer': AQI_SOURCE_LAYER,
    maxzoom: MIN_ICON_ZOOM_LEVEL,
    paint: {
      'circle-radius': 0,
      'circle-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
      'circle-blur': 1,
    },
    filter,
  };
};
